import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchPage, useSearchResults, useSearchContext } from '../contexts/SearchContext';
import { FormattedMessage } from 'react-intl';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useThrottledResize } from '../hooks/useThrottledResize';

/** Find the first scrollable parent (overflow-y auto/scroll) and scroll it and window to top. */
function scrollSearchResultsToTop(fromElement: HTMLElement | null): void {
  if (typeof window === 'undefined') return;
  window.scrollTo({ top: 0, behavior: 'auto' });
  let el: HTMLElement | null = fromElement;
  while (el) {
    const style = window.getComputedStyle(el);
    const overflowY = style.overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      if (el.scrollHeight > el.clientHeight) {
        el.scrollTop = 0;
        break;
      }
    }
    el = el.parentElement;
  }
}

function CustomPagination() {
  const { page, setPage } = useSearchPage();
  const { results } = useSearchResults();
  const { getPageUrl } = useSearchContext();
  const nbPages = Math.max(1, Number(results?.nbPages || 1));
  const currentRefinement = Math.max(0, Math.min(page, nbPages - 1));
  // Build a compact window of 5 pages centered on current page
  const windowSize = 5;
  const half = Math.floor(windowSize / 2);
  let start = Math.max(0, currentRefinement - half);
  let end = start + (windowSize - 1);
  if (end > nbPages - 1) {
    end = nbPages - 1;
    start = Math.max(0, end - (windowSize - 1));
  }
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
  const isFirstPage = currentRefinement <= 0;
  const isLastPage = currentRefinement >= nbPages - 1;
  const [isNavigating, setIsNavigating] = React.useState(false);
  const [navigatingToPage, setNavigatingToPage] = React.useState<number | null>(null);
  
  // Track the last page that was set to prevent duplicate calls
  const lastSetPageRef = useRef<number | null>(null);
  const refineTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  
  const refine = useCallback((p: number) => {
    // Prevent duplicate calls to the same page
    if (lastSetPageRef.current === p) {
      return;
    }
    
    // Clear any pending timeout
    if (refineTimeoutRef.current) {
      clearTimeout(refineTimeoutRef.current);
    }
    
    // Clamp the page number to valid range
    const targetPage = Math.max(0, Math.min(p, nbPages - 1));
    
    // Prevent setting the same page we're already on
    if (targetPage === currentRefinement) {
      return;
    }
    
    // Mark this page as being set
    lastSetPageRef.current = targetPage;
    
    // Show immediate feedback
    setIsNavigating(true);
    setNavigatingToPage(targetPage);
    
    // Update page immediately (optimistic update)
    setPage(targetPage);

    // Scroll to top: both window and the layout's scroll container (overflow-y-auto div) so we start at top of the new page
    scrollSearchResultsToTop(navRef.current);

    // Hide loading after a short delay (results will update)
    refineTimeoutRef.current = setTimeout(() => {
      setIsNavigating(false);
      setNavigatingToPage(null);
      // Clear the ref after navigation completes to allow future navigation to same page
      lastSetPageRef.current = null;
      // Scroll again after results have rendered in case layout shifted
      scrollSearchResultsToTop(navRef.current);
    }, 300);
  }, [nbPages, currentRefinement, setPage]);
  const createURL = (p: number) => getPageUrl(p);

  const firstPageIndex = 0;
  const previousPageIndex = currentRefinement - 1;
  const nextPageIndex = currentRefinement + 1;
  const lastPageIndex = nbPages - 1;

  // Use state to track mobile status, defaulting to false for SSR
  const [isMobile, setIsMobile] = useState(false);

  // Set mobile status on client-side only
  const checkMobile = useCallback(() => {
    if (typeof window !== 'undefined') {
      setIsMobile(window.innerWidth < 640);
    }
  }, []);
  
  useEffect(() => {
    // Initial check
    checkMobile();
  }, [checkMobile]);
  
  // Use throttled resize to prevent lag
  useThrottledResize(checkMobile);

  // Keyboard shortcut: j (previous), k (next)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handler = (e: KeyboardEvent) => {
      // Ignore if focused in input/textarea/select
      if (
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement ||
        document.activeElement instanceof HTMLSelectElement
      ) {
        return;
      }
      if (e.key === 'j' && !isFirstPage) {
        e.preventDefault();
        refine(previousPageIndex);
      } else if (e.key === 'k' && !isLastPage) {
        e.preventDefault();
        refine(nextPageIndex);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isFirstPage, isLastPage, nextPageIndex, previousPageIndex, refine]);
  
  // Sync the ref with the actual page state to handle external updates (e.g., from URL)
  useEffect(() => {
    // If the page changed externally (not from our refine function), reset the ref
    if (lastSetPageRef.current !== null && lastSetPageRef.current !== currentRefinement) {
      lastSetPageRef.current = null;
    }
  }, [currentRefinement]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (refineTimeoutRef.current) {
        clearTimeout(refineTimeoutRef.current);
      }
    };
  }, []);

  return (
    <nav ref={navRef} className="flex justify-center py-4 md:py-6" aria-label="Pagination">
      <div className="flex flex-col items-center gap-3 w-full max-w-4xl px-4">
        {/* Mobile: Previous/Next buttons at top */}
        {isMobile && (
          <div className="flex items-stretch justify-between w-full gap-3" role="list">
            <PaginationItem 
              isDisabled={isFirstPage} 
              href={createURL(previousPageIndex)} 
              onClick={() => refine(previousPageIndex)}
              isLoading={isNavigating && navigatingToPage === previousPageIndex}
              className="flex-1 max-w-[140px] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/80 active:bg-gray-100 dark:active:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
            >
              <div className="flex items-center justify-center gap-2">
                <ChevronLeftIcon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                <span className="font-semibold">
                  <FormattedMessage id="pagination.previous" defaultMessage="Previous" description="Button label to go to the previous page of search results" />
                </span>
              </div>
            </PaginationItem>
            <div className="flex-1 text-center flex items-center justify-center">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {currentRefinement + 1} / {nbPages}
              </span>
            </div>
            <PaginationItem 
              isDisabled={isLastPage} 
              href={createURL(nextPageIndex)} 
              onClick={() => refine(nextPageIndex)}
              isLoading={isNavigating && navigatingToPage === nextPageIndex}
              className="flex-1 max-w-[140px] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/80 active:bg-gray-100 dark:active:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
            >
              <div className="flex items-center justify-center gap-2">
                <span className="font-semibold">
                  <FormattedMessage id="pagination.next" defaultMessage="Next" description="Button label to go to the next page of search results" />
                </span>
                <ChevronRightIcon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              </div>
            </PaginationItem>
          </div>
        )}

        {/* Page numbers - divs instead of ul/li to avoid list marker dots in some browsers */}
        <div className="flex justify-center items-center gap-2 flex-wrap" role="list">
          {!isMobile && (
            <>
              <PaginationItem 
                isDisabled={isFirstPage} 
                href={createURL(firstPageIndex)} 
                onClick={() => refine(firstPageIndex)}
                isLoading={isNavigating && navigatingToPage === firstPageIndex}
                className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/80 active:bg-gray-100 dark:active:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
              >
                <FormattedMessage id="pagination.first" defaultMessage="First" description="Button label to go to the first page of search results" />
              </PaginationItem>
              <PaginationItem 
                isDisabled={isFirstPage} 
                href={createURL(previousPageIndex)} 
                onClick={() => refine(previousPageIndex)}
                isLoading={isNavigating && navigatingToPage === previousPageIndex}
                className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/80 active:bg-gray-100 dark:active:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
              >
                <div className="flex items-center gap-1.5">
                  <ChevronLeftIcon className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                  <FormattedMessage id="pagination.previous" defaultMessage="Previous" description="Button label to go to the previous page of search results" />
                </div>
              </PaginationItem>
            </>
          )}

          {pages.map((page) => {
            const label = page + 1;
            const isActive = currentRefinement === page;

            return (
              <PaginationItem
                key={page}
                isDisabled={false}
                aria-label={`Page ${label}`}
                href={createURL(page)}
                onClick={() => refine(page)}
                isLoading={isNavigating && navigatingToPage === page}
                className={isActive 
                  ? 'bg-green-500 dark:bg-green-600 text-white dark:text-white font-semibold shadow-md dark:shadow-green-900/50 border border-green-600 dark:border-green-500' 
                  : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/80 active:bg-gray-100 dark:active:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }
              >
                {label}
              </PaginationItem>
            );
          })}

          {!isMobile && (
            <>
              <PaginationItem 
                isDisabled={isLastPage} 
                href={createURL(nextPageIndex)} 
                onClick={() => refine(nextPageIndex)}
                isLoading={isNavigating && navigatingToPage === nextPageIndex}
                className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/80 active:bg-gray-100 dark:active:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
              >
                <div className="flex items-center gap-1.5">
                  <FormattedMessage id="pagination.next" defaultMessage="Next" description="Button label to go to the next page of search results" />
                  <ChevronRightIcon className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                </div>
              </PaginationItem>
              <PaginationItem 
                isDisabled={isLastPage} 
                href={createURL(lastPageIndex)} 
                onClick={() => refine(lastPageIndex)}
                isLoading={isNavigating && navigatingToPage === lastPageIndex}
                className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/80 active:bg-gray-100 dark:active:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
              >
                <FormattedMessage id="pagination.last" defaultMessage="Last" description="Button label to go to the last page of search results" />
              </PaginationItem>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

type PaginationItemProps = Omit<React.ComponentProps<'a'>, 'onClick'> & {
  onClick: NonNullable<React.ComponentProps<'a'>['onClick']>;
  isDisabled: boolean;
};

function PaginationItem({ isDisabled, href, onClick, className = '', children, isLoading = false, ...props }: PaginationItemProps & { isLoading?: boolean }) {
  const baseClasses = 'min-w-[44px] min-h-[44px] md:min-w-[48px] md:min-h-[48px] px-4 md:px-5 py-2.5 md:py-3 rounded-xl md:rounded-2xl text-sm md:text-base font-medium transition-all duration-200 ease-out active:scale-95 flex items-center justify-center touch-manipulation select-none';

  if (isDisabled) {
    return (
      <div role="listitem">
        <span 
          className={`${baseClasses} bg-gray-100 dark:bg-gray-800/50 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700/50 cursor-not-allowed opacity-60 dark:opacity-50 ${className}`} 
          {...props}
        >
          {children}
        </span>
      </div>
    );
  }

  return (
    <div role="listitem">
      <a
        href={href}
        className={`${baseClasses} cursor-pointer shadow-sm dark:shadow-gray-900/50 hover:shadow-md dark:hover:shadow-gray-900/70 ${isLoading ? 'opacity-75 pointer-events-none' : ''} ${className}`}
        style={{
          WebkitTapHighlightColor: 'transparent',
        }}
        onClick={(event) => {
          if (isModifierClick(event)) {
            return;
          }

          event.preventDefault();
          event.stopPropagation(); // Prevent event bubbling that could cause double calls

          onClick(event);
        }}
        {...props}
      >
        {isLoading ? (
          <div className="flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin opacity-70" />
          </div>
        ) : (
          children
        )}
      </a>
    </div>
  );
}

function isModifierClick(event: React.MouseEvent) {
  const isMiddleClick = event.button === 1;

  return Boolean(isMiddleClick || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey);
}

export default CustomPagination;
